import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { BookOpen, Loader2, Mail, Lock, User, Building2, Leaf, BarChart3, Shield, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Auth({ onLogin }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({
    email: "",
    password: "",
    name: "",
    company_name: "",
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginData.email || !loginData.password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      toast.success(`Welcome back, ${response.data.user.name}!`);
      onLogin(response.data.user);
      // Redirect based on role
      if (response.data.user.role === "platform_owner") {
        navigate("/admin/companies");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerData.email || !registerData.password || !registerData.name) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, registerData);
      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      toast.success("Account created successfully!");
      onLogin(response.data.user);
      // Redirect based on role
      if (response.data.user.role === "platform_owner") {
        navigate("/admin/companies");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Leaf, title: "Carbon Tracking", desc: "AI-powered emission estimates" },
    { icon: BarChart3, title: "Smart Reports", desc: "CSRD compliance ready" },
    { icon: Shield, title: "Audit Ready", desc: "Financial-grade accuracy" },
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding & Features */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-[#1a2e22]">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#00c853] rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#00c853] rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
          <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-emerald-400 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        </div>
        
        {/* Grid Pattern Overlay */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />

        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[#00c853] flex items-center justify-center shadow-lg shadow-[#00c853]/30">
              <BookOpen className="w-6 h-6 text-[#1a2e22]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white font-['Outfit']">Ledger Green</h1>
              <p className="text-white/50 text-sm">Sustainability Portal</p>
            </div>
          </div>

          {/* Main Content */}
          <div className="space-y-8">
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold text-white font-['Outfit'] leading-tight">
                Transform your
                <br />
                <span className="text-[#00c853]">financial data</span>
                <br />
                into climate action
              </h2>
              <p className="mt-6 text-white/60 text-lg max-w-md leading-relaxed">
                AI-powered sustainability accounting that links every transaction to its carbon footprint.
              </p>
            </div>

            {/* Features */}
            <div className="space-y-4">
              {features.map((feature, idx) => (
                <div 
                  key={idx}
                  className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-colors"
                  style={{ animationDelay: `${idx * 100}ms` }}
                >
                  <div className="w-12 h-12 rounded-xl bg-[#00c853]/20 flex items-center justify-center">
                    <feature.icon className="w-6 h-6 text-[#00c853]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">{feature.title}</h3>
                    <p className="text-white/50 text-sm">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
        </div>
      </div>

      {/* Right Side - Auth Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-gradient-to-br from-[#f2f7f4] to-[#e8f0eb]">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#1a2e22] mb-4">
              <BookOpen className="w-8 h-8 text-[#00c853]" />
            </div>
            <h1 className="text-2xl font-bold text-[#1a2e22] font-['Outfit']">Ledger Green</h1>
            <p className="text-[#5c6b63]">Sustainability Portal</p>
          </div>

          <Card className="border-0 shadow-2xl shadow-[#1a2e22]/10 overflow-hidden">
            <CardContent className="p-0">
              <Tabs defaultValue="login" className="w-full">
                <TabsList className="grid w-full grid-cols-2 rounded-none h-14 bg-[#f2f7f4]">
                  <TabsTrigger 
                    value="login" 
                    className="text-base data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-none h-full"
                    data-testid="login-tab"
                  >
                    Sign In
                  </TabsTrigger>
                  <TabsTrigger 
                    value="register" 
                    className="text-base data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-none h-full"
                    data-testid="register-tab"
                  >
                    Create Account
                  </TabsTrigger>
                </TabsList>

                {/* Login Form */}
                <TabsContent value="login" className="p-8 mt-0 bg-white">
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-[#1a2e22] font-['Outfit']">Welcome back</h2>
                    <p className="text-[#5c6b63] mt-1">Enter your credentials to continue</p>
                  </div>
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email" className="text-[#1a2e22] font-medium">Email</Label>
                      <div className="relative group">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#c8d4cc] group-focus-within:text-[#00c853] transition-colors" />
                        <Input
                          id="login-email"
                          type="email"
                          placeholder="you@company.com"
                          value={loginData.email}
                          onChange={(e) =>
                            setLoginData({ ...loginData, email: e.target.value })
                          }
                          className="pl-12 h-12 bg-[#f8faf9] border-[#e0e7e3] focus:border-[#00c853] focus:ring-[#00c853]/20 focus:bg-white transition-all"
                          data-testid="login-email"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password" className="text-[#1a2e22] font-medium">Password</Label>
                      <div className="relative group">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#c8d4cc] group-focus-within:text-[#00c853] transition-colors" />
                        <Input
                          id="login-password"
                          type="password"
                          placeholder="••••••••"
                          value={loginData.password}
                          onChange={(e) =>
                            setLoginData({ ...loginData, password: e.target.value })
                          }
                          className="pl-12 h-12 bg-[#f8faf9] border-[#e0e7e3] focus:border-[#00c853] focus:ring-[#00c853]/20 focus:bg-white transition-all"
                          data-testid="login-password"
                        />
                      </div>
                    </div>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full h-12 bg-[#1a2e22] hover:bg-[#2d4a3e] text-white text-base font-medium rounded-xl transition-all hover:shadow-lg hover:shadow-[#1a2e22]/20 group mt-2"
                      data-testid="login-submit"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          Sign In
                          <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </Button>
                  </form>
                </TabsContent>

                {/* Register Form */}
                <TabsContent value="register" className="p-8 mt-0 bg-white">
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-[#1a2e22] font-['Outfit']">Get started</h2>
                    <p className="text-[#5c6b63] mt-1">Create your sustainability account</p>
                  </div>
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name" className="text-[#1a2e22] font-medium">Full Name *</Label>
                      <div className="relative group">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#c8d4cc] group-focus-within:text-[#00c853] transition-colors" />
                        <Input
                          id="register-name"
                          type="text"
                          placeholder="John Smith"
                          value={registerData.name}
                          onChange={(e) =>
                            setRegisterData({ ...registerData, name: e.target.value })
                          }
                          className="pl-12 h-12 bg-[#f8faf9] border-[#e0e7e3] focus:border-[#00c853] focus:ring-[#00c853]/20 focus:bg-white transition-all"
                          data-testid="register-name"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-email" className="text-[#1a2e22] font-medium">Email *</Label>
                      <div className="relative group">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#c8d4cc] group-focus-within:text-[#00c853] transition-colors" />
                        <Input
                          id="register-email"
                          type="email"
                          placeholder="you@company.com"
                          value={registerData.email}
                          onChange={(e) =>
                            setRegisterData({ ...registerData, email: e.target.value })
                          }
                          className="pl-12 h-12 bg-[#f8faf9] border-[#e0e7e3] focus:border-[#00c853] focus:ring-[#00c853]/20 focus:bg-white transition-all"
                          data-testid="register-email"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-company" className="text-[#1a2e22] font-medium">Company</Label>
                      <div className="relative group">
                        <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#c8d4cc] group-focus-within:text-[#00c853] transition-colors" />
                        <Input
                          id="register-company"
                          type="text"
                          placeholder="Company Ltd"
                          value={registerData.company_name}
                          onChange={(e) =>
                            setRegisterData({ ...registerData, company_name: e.target.value })
                          }
                          className="pl-12 h-12 bg-[#f8faf9] border-[#e0e7e3] focus:border-[#00c853] focus:ring-[#00c853]/20 focus:bg-white transition-all"
                          data-testid="register-company"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password" className="text-[#1a2e22] font-medium">Password *</Label>
                      <div className="relative group">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#c8d4cc] group-focus-within:text-[#00c853] transition-colors" />
                        <Input
                          id="register-password"
                          type="password"
                          placeholder="••••••••"
                          value={registerData.password}
                          onChange={(e) =>
                            setRegisterData({ ...registerData, password: e.target.value })
                          }
                          className="pl-12 h-12 bg-[#f8faf9] border-[#e0e7e3] focus:border-[#00c853] focus:ring-[#00c853]/20 focus:bg-white transition-all"
                          data-testid="register-password"
                        />
                      </div>
                    </div>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full h-13 bg-[#00c853] hover:bg-[#00a854] text-[#1a2e22] text-base font-semibold rounded-xl transition-all hover:shadow-lg hover:shadow-[#00c853]/30 group mt-2"
                      data-testid="register-submit"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          Create Account
                          <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
